import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useQuery } from '@tanstack/react-query';
import { formatProper } from '@utils/text.util';
import { TRL_BUCKETS } from '@utils/trl-buckets.util';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AssessmentSelectDto, AssessmentType, TechnologySelectDto } from '../../types/api.types';
import { AreaBarChartComponent } from './area-bar-chart.component';
import { Tile, TileProps } from './tile.component';

type TechnologiesTrlSpectrumBarProps = TileProps & {
  technologies: TechnologySelectDto[];
};

export const TechnologiesTrlSpectrumTile: React.FC<TechnologiesTrlSpectrumBarProps> = (props) => {
  const { technologies, ...tileProps } = props;
  const navigate = useNavigate();

  const onNavigateToDetails = (id: string): void => {
    navigate(`/technology/${id}`);
  };

  const assessmentQueryOptions = useAssessmentsQueryOptions({ type: [AssessmentType.TRL] });
  const { data: technologyAssessments = [], isPending } = useQuery(assessmentQueryOptions);

  const assessmentsByTechId = React.useMemo(() => {
    const map = new Map<string, AssessmentSelectDto[]>();
    technologyAssessments.forEach((assessment) => {
      const existing = map.get(assessment.technologyId) || [];
      existing.push(assessment);
      map.set(assessment.technologyId, existing);
    });
    return map;
  }, [technologyAssessments]);

  if (isPending) {
    return null;
  }

  type TrlSpectrumItem = {
    onClick: () => void;
    subtitle?: string;
    title: string;
    valueRange: { max: number; min: number };
  };

  const items: TrlSpectrumItem[] = [];

  for (const technology of technologies) {
    const trlAssessments = [...(assessmentsByTechId.get(technology.id) || [])].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );

    if (trlAssessments.length === 0) continue;

    const seenKeys = new Set<string>();
    const uniqueScores: number[] = [];

    for (const assessment of trlAssessments) {
      const key = `${assessment.use.id}-${assessment.domain.id}`;
      if (seenKeys.has(key)) continue;

      seenKeys.add(key);
      uniqueScores.push(assessment.score);
    }

    if (uniqueScores.length === 0) continue;

    const valueRange = {
      max: Math.max(...uniqueScores),
      min: Math.min(...uniqueScores),
    };

    items.push({
      onClick: () => onNavigateToDetails(technology.id),
      subtitle: formatProper(technology.type),
      title: technology.name,
      valueRange,
    });
  }

  if (items.length === 0) return null;

  return (
    <Tile {...tileProps}>
      <AreaBarChartComponent
        buckets={TRL_BUCKETS}
        items={items}
        valueRange={{ max: 9, min: 1 }}
      />
    </Tile>
  );
};
